import dayjs, { Dayjs } from 'dayjs';

import { EligibilityBlock, IsoDateTime } from 'types/students';

import { formatCents } from 'utils/formatCurrency';

/**
 * Check if a specific day is blocked by any eligibility block
 */
export function isDayBlocked(day: Dayjs, blocks: EligibilityBlock[]): boolean {
  const dayStart = day.startOf('day');
  const dayEnd = day.endOf('day');

  return blocks.some((block) => {
    // blocked_indefinitely blocks everything
    if (block.type === 'blocked_indefinitely') return true;

    // Check if block overlaps with this day
    const blockStart = block.blocksFrom ? dayjs(block.blocksFrom) : null;
    const blockEnd = block.blocksUntil ? dayjs(block.blocksUntil) : null;

    // Logic for each block type
    switch (block.type) {
      case 'blocked_until':
        return blockEnd ? dayEnd.isBefore(blockEnd) || dayEnd.isSame(blockEnd, 'day') : false;
      case 'blocked_from':
        return blockStart ? dayStart.isAfter(blockStart) || dayStart.isSame(blockStart, 'day') : false;
      case 'blocked_between': {
        // Check if day is within the blocked window
        const dayAfterStart = !blockStart || dayStart.isAfter(blockStart) || dayStart.isSame(blockStart, 'day');
        const dayBeforeEnd = !blockEnd || dayEnd.isBefore(blockEnd) || dayEnd.isSame(blockEnd, 'day');
        return dayAfterStart && dayBeforeEnd;
      }
      default:
        return false;
    }
  });
}

/**
 * Check if a block affects a specific period
 */
export function doesBlockAffectPeriod(block: EligibilityBlock, periodStart: string, periodEnd: string): boolean {
  const pStart = dayjs(periodStart);
  const pEnd = dayjs(periodEnd);

  // blocked_indefinitely blocks everything
  if (block.type === 'blocked_indefinitely') return true;

  const blockStart = block.blocksFrom ? dayjs(block.blocksFrom) : null;
  const blockEnd = block.blocksUntil ? dayjs(block.blocksUntil) : null;

  switch (block.type) {
    case 'blocked_until':
      // Period is blocked if it starts before the blocksUntil date
      return blockEnd ? pStart.isBefore(blockEnd) : false;
    case 'blocked_from':
      // Period is blocked if it starts after the blocksFrom date
      return blockStart ? pStart.isAfter(blockStart) || pStart.isSame(blockStart) : false;
    case 'blocked_between': {
      // Check if period overlaps with blocked window
      // Period is NOT blocked if it ends before window starts OR starts after window ends
      const periodBeforeWindow = blockStart && pEnd.isBefore(blockStart);
      const periodAfterWindow = blockEnd && pStart.isAfter(blockEnd);
      return !(periodBeforeWindow || periodAfterWindow);
    }
    default:
      return false;
  }
}

/**
 * Prioritize blocks to show the most urgent ones first
 * Priority: until_resolved (by action type) > time-based (by earliest date)
 */
export function prioritizeBlocks(blocks: EligibilityBlock[]): EligibilityBlock[] {
  if (blocks.length === 0) return [];

  // Separate blocked_indefinitely blocks
  const untilResolved = blocks.filter((b) => b.type === 'blocked_indefinitely');
  const timeBasedBlocks = blocks.filter((b) => b.type !== 'blocked_indefinitely');

  // Prioritize until_resolved by action type
  const actionPriority: Record<string, number> = {
    PAY: 1,
    UPDATE_PERMIT: 2,
    CANCEL_BOOKING: 3,
    CONTACT_ADMIN: 4,
    WAIT: 5,
    NONE: 6,
  };

  untilResolved.sort((a, b) => {
    const aPriority = actionPriority[a.resolution.action] ?? 999;
    const bPriority = actionPriority[b.resolution.action] ?? 999;
    return aPriority - bPriority;
  });

  // Sort time-based by earliest blocking date
  timeBasedBlocks.sort((a, b) => {
    const aDate = a.blocksFrom ?? a.blocksUntil;
    const bDate = b.blocksFrom ?? b.blocksUntil;
    if (!aDate) return 1;
    if (!bDate) return -1;
    return dayjs(aDate).diff(dayjs(bDate));
  });

  // Return until_resolved first, then time-based
  return [...untilResolved, ...timeBasedBlocks];
}

/**
 * Get human-readable time until eligible
 * Returns null for until_resolved blocks or blocks with no end date
 */
export function getTimeUntilEligible(block: EligibilityBlock): string | null {
  if (block.type === 'blocked_indefinitely') return null;
  if (!block.blocksUntil) return null;

  const now = dayjs();
  const until = dayjs(block.blocksUntil);

  // If the block has already ended, return null
  if (now.isAfter(until)) return null;

  return until.from(now); // Uses dayjs relativeTime plugin (e.g., "in 2 hours")
}

/**
 * Determines if a given eligibility block is currently active (blocking the user right now).
 *
 * @param block - The eligibility block to check
 * @param now - The current date/time (defaults to now)
 * @returns True if the block is currently preventing booking
 */
export function isBlockActive(block: EligibilityBlock, now: Dayjs = dayjs()): boolean {
  // "blocked_indefinitely" blocks are always active until manually resolved
  if (block.type === 'blocked_indefinitely') {
    return true;
  }

  // "blocked_until" blocks until a specific date/time
  if (block.type === 'blocked_until' && block.blocksUntil) {
    return now.isBefore(dayjs(block.blocksUntil));
  }

  // "blocked_from" blocks starting from a specific date/time
  if (block.type === 'blocked_from' && block.blocksFrom) {
    return now.isAfter(dayjs(block.blocksFrom)) || now.isSame(dayjs(block.blocksFrom));
  }

  // "blocked_between" blocks between two dates/times
  if (block.type === 'blocked_between' && block.blocksFrom && block.blocksUntil) {
    const from = dayjs(block.blocksFrom);
    const until = dayjs(block.blocksUntil);
    return (now.isAfter(from) || now.isSame(from)) && (now.isBefore(until) || now.isSame(until));
  }

  return false;
}

/**
 * Determines if the student is currently eligible to book (no active blocks).
 *
 * @param blocks - Array of eligibility blocks
 * @param now - The current date/time (defaults to now)
 * @returns True if eligible to book now
 */
export function isEligibleNow(blocks: EligibilityBlock[], now: Dayjs = dayjs()): boolean {
  return !blocks.some((block) => isBlockActive(block, now));
}

/**
 * Calculates the next date/time when the student will become eligible again.
 *
 * @param blocks - Array of eligibility blocks
 * @param now - The current date/time (defaults to now)
 * @returns ISO timestamp of next eligibility, or null if no future eligibility
 */
export function getNextEligibleAt(blocks: EligibilityBlock[], now: Dayjs = dayjs()): IsoDateTime | null {
  let earliestDate: Dayjs | null = null;

  for (const block of blocks) {
    // "blocked_indefinitely" blocks have no automatic resolution date
    if (block.type === 'blocked_indefinitely') {
      continue;
    }

    // "blocked_until" blocks end at blocksUntil
    if (block.type === 'blocked_until' && block.blocksUntil) {
      const endDate = dayjs(block.blocksUntil);
      if (now.isBefore(endDate)) {
        if (!earliestDate || endDate.isBefore(earliestDate)) {
          earliestDate = endDate;
        }
      }
    }

    // "blocked_between" blocks end at blocksUntil
    if (block.type === 'blocked_between' && block.blocksFrom && block.blocksUntil) {
      const from = dayjs(block.blocksFrom);
      const endDate = dayjs(block.blocksUntil);
      if ((now.isAfter(from) || now.isSame(from)) && (now.isBefore(endDate) || now.isSame(endDate))) {
        if (!earliestDate || endDate.isBefore(earliestDate)) {
          earliestDate = endDate;
        }
      }
    }

    // "blocked_from" blocks never end (they started in the past and continue forever)
    // so we skip them
  }

  return earliestDate ? earliestDate.toISOString() : null;
}

/**
 * Filters blocks to only include those that are currently active.
 *
 * @param blocks - Array of eligibility blocks
 * @param now - The current date/time (defaults to now)
 * @returns Array of currently active blocks
 */
export function getActiveBlocks(blocks: EligibilityBlock[], now: Dayjs = dayjs()): EligibilityBlock[] {
  return blocks.filter((block) => isBlockActive(block, now));
}

/**
 * Generate a summary eligibility block for balance violations.
 * Returns a single block if any balance restriction is violated, null otherwise.
 *
 * The message adapts based on whether the student has booked their first period:
 *
 * **Hasn't booked first period:**
 * - Only mentions "first period" and/or "last period" restrictions
 * - Ignores "other" restriction since it's not relevant until first period is booked
 *
 * **Has booked first period:**
 * - Uses generic "periods" for the "other" restriction
 * - Mentions "last period" if that restriction is violated
 * - Ignores "first period" restriction since it's already been booked
 *
 * @param params.currentBalance - The student's current balance in cents
 * @param params.balanceRestrictions - Maximum allowed balance for each period type (null means no restriction)
 * @param params.hasBookedFirstPeriod - Whether the student has already booked at least one period
 * @returns A summary eligibility block or null if no relevant violations
 */
export function generateSummaryBalanceBlock(params: {
  currentBalance: number; // cents
  balanceRestrictions: {
    firstPeriod: number | null;
    lastPeriod: number | null;
    other: number | null;
  };
  hasBookedFirstPeriod: boolean;
}): EligibilityBlock | null {
  const { currentBalance, balanceRestrictions, hasBookedFirstPeriod } = params;

  // Check which restrictions are violated
  const violatesFirst = balanceRestrictions.firstPeriod !== null && currentBalance > balanceRestrictions.firstPeriod;
  const violatesLast = balanceRestrictions.lastPeriod !== null && currentBalance > balanceRestrictions.lastPeriod;
  const violatesOther = balanceRestrictions.other !== null && currentBalance > balanceRestrictions.other;

  // No violations
  if (!violatesFirst && !violatesLast && !violatesOther) {
    return null;
  }

  // Build list of affected period types based on context
  // - If hasn't booked first period: only mention "first period" and/or "last period" (ignore other)
  // - If has booked first period: use "periods" for other, "last period" for last
  const affectedTypes: string[] = [];

  if (!hasBookedFirstPeriod) {
    // Student hasn't booked first period yet
    if (violatesFirst) {
      affectedTypes.push('your first period');
    }
    if (violatesLast) {
      affectedTypes.push('your last period');
    }
    // Don't mention "other" restriction - they need to book first period first
  } else {
    // Student has already booked first period
    if (violatesOther && violatesLast) {
      affectedTypes.push('periods'); // Combine both into a generic term
    } else if (violatesOther) {
      affectedTypes.push('periods');
    } else if (violatesLast) {
      affectedTypes.push('your last period');
    }
    // Don't mention "first" restriction - they already booked it
  }

  // If no relevant affected types for current state, don't show the alert
  if (affectedTypes.length === 0) {
    return null;
  }

  // Build the message
  let reasonMessage: string;
  const periodTypesText = affectedTypes.join(' and ');

  // Check if all relevant restrictions are violated
  const allRelevantViolated = hasBookedFirstPeriod ? violatesOther && violatesLast : violatesFirst && violatesLast;

  if (allRelevantViolated && affectedTypes.length > 1) {
    reasonMessage = `Your current balance of ${formatCents(currentBalance)} prevents you from booking ${periodTypesText}.`;
  } else {
    reasonMessage = `Your current balance of ${formatCents(currentBalance)} may prevent you from booking ${periodTypesText}.`;
  }

  return {
    id: 'balance-summary',
    type: 'blocked_indefinitely',
    blocksFrom: null,
    blocksUntil: null,
    reason: {
      code: 'BALANCE_RESTRICTION',
      title: 'Balance Restriction',
      message: reasonMessage,
      data: {
        currentBalance,
        affectedTypes,
      },
    },
    resolution: {
      action: 'PAY',
      message: 'Make a payment to book these periods.',
      url: '/student/ledger',
    },
  };
}
